import type { Repo } from './repo';

export type PersistState = 'granted' | 'denied' | 'unsupported';

export interface StorageStatus {
  persist: PersistState;
  /** When persist() was first requested on this origin. */
  firstRequestedAt?: number;
  usage?: number;
  quota?: number;
}

const KEY = 'persistRequestedAt';

/**
 * Asks the browser not to evict this origin's data. Requested on first launch,
 * and again on later launches while it is still not granted (Chrome grants it
 * more readily once the app is installed or used often).
 */
export async function ensurePersistentStorage(repo: Repo): Promise<StorageStatus> {
  const sm = navigator.storage;
  if (!sm?.persist || !sm.persisted) return { persist: 'unsupported' };
  let firstRequestedAt = await repo.getSetting<number>(KEY);
  let granted = await sm.persisted();
  if (!granted) {
    granted = await sm.persist();
    if (firstRequestedAt === undefined) {
      firstRequestedAt = Date.now();
      await repo.setSetting(KEY, firstRequestedAt);
    }
  }
  const est = await sm.estimate?.().catch(() => undefined);
  return {
    persist: granted ? 'granted' : 'denied',
    ...(firstRequestedAt !== undefined ? { firstRequestedAt } : {}),
    ...(est?.usage !== undefined ? { usage: est.usage } : {}),
    ...(est?.quota !== undefined ? { quota: est.quota } : {}),
  };
}
